import {
  Badge,
  Box,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  IconRefresh,
  IconCloudUpload,
  IconCloudCheck,
  IconAlertCircle,
  IconPalette,
  IconLanguage,
  IconLayoutSidebar,
  IconWorld,
  IconUsers,
} from '@tabler/icons-react';
import Tabs from '../../components/ui/navigation/Tabs';
import { usePreferencesStore } from '../../stores';
import { usePreferencesSync } from './hooks/usePreferencesSync';
import AppearanceSection from './components/AppearanceSection';
import AccessibilitySection from './components/AccessibilitySection';
import LanguageRegionSection from './components/LanguageRegionSection';
import PresenceSection from './components/PresenceSection';
import MenuPreferencesSection from './components/MenuPreferencesSection';
import LayoutPreferencesSection from './components/LayoutPreferencesSection';

function formatDistanceToNow(date: Date): string {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function SettingsPage() {
  const resetPreferences = usePreferencesStore((state) => state.resetPreferences);
  const { syncState, syncToCloud } = usePreferencesSync();

  const handleReset = () => {
    if (confirm('Reset all settings to defaults? This will clear all your customizations.')) {
      resetPreferences();
    }
  };

  return (
      <Stack gap="lg">
        {/* Sticky Header with Actions */}
        <Paper
          py="md"
          px="lg"
          radius="md"
          withBorder
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'var(--mantine-color-body)',
          }}
        >
          <Group justify="space-between" align="center" wrap="wrap">
            <Box>
              <Title order={2}>Settings</Title>
              <Group gap="xs" mt={4}>
                <Text size="sm" c="dimmed">
                  Customize your experience and preferences
                </Text>
                {syncState.lastSynced && syncState.status === 'idle' && (
                  <Badge size="sm" variant="light" color="blue">
                    Synced {formatDistanceToNow(syncState.lastSynced)}
                  </Badge>
                )}
                {syncState.status === 'success' && (
                  <Badge size="sm" variant="light" color="green" leftSection={<IconCloudCheck size={12} />}>
                    Synced
                  </Badge>
                )}
                {syncState.status === 'error' && (
                  <Badge size="sm" variant="light" color="red" leftSection={<IconAlertCircle size={12} />}>
                    {syncState.error || 'Sync failed'}
                  </Badge>
                )}
              </Group>
            </Box>
            <Group gap="sm">
              <Button
                variant="default"
                size="sm"
                leftSection={<IconRefresh size={16} />}
                onClick={handleReset}
              >
                Reset
              </Button>
              <Button
                size="sm"
                leftSection={
                  syncState.status === 'success' ? (
                    <IconCloudCheck size={16} />
                  ) : (
                    <IconCloudUpload size={16} />
                  )
                }
                onClick={syncToCloud}
                loading={syncState.status === 'syncing'}
                color={syncState.status === 'success' ? 'green' : undefined}
              >
                {syncState.status === 'syncing'
                  ? 'Syncing...'
                  : syncState.status === 'success'
                    ? 'Synced!'
                    : 'Sync to Cloud'}
              </Button>
            </Group>
          </Group>
        </Paper>

        {/* Tabbed Settings */}
        <Tabs tabsVariant="gradient" defaultValue="appearance" keepMounted={false}>
          <Tabs.List mb="lg">
            <Tabs.Tab value="appearance" leftSection={<IconPalette size={16} />}>
              Appearance
            </Tabs.Tab>
            <Tabs.Tab value="language" leftSection={<IconLanguage size={16} />}>
              Language & Region
            </Tabs.Tab>
            <Tabs.Tab value="navigation" leftSection={<IconLayoutSidebar size={16} />}>
              Navigation & Layout
            </Tabs.Tab>
            <Tabs.Tab value="accessibility" leftSection={<IconWorld size={16} />}>
              Accessibility
            </Tabs.Tab>
            <Tabs.Tab value="presence" leftSection={<IconUsers size={16} />}>
              Presence
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="appearance">
            <Paper p="lg" radius="md" withBorder>
              <AppearanceSection />
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="language">
            <Paper p="lg" radius="md" withBorder>
              <LanguageRegionSection />
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="navigation">
            <Stack gap="lg">
              <Paper p="lg" radius="md" withBorder>
                <MenuPreferencesSection />
              </Paper>
              <Paper p="lg" radius="md" withBorder>
                <LayoutPreferencesSection />
              </Paper>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="accessibility">
            <Paper p="lg" radius="md" withBorder>
              <AccessibilitySection />
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="presence">
            <Paper p="lg" radius="md" withBorder>
              <PresenceSection />
            </Paper>
          </Tabs.Panel>
        </Tabs>
      </Stack>
  );
}
